/* eslint-disable indent */
/* eslint-disable no-unused-expressions */
import { useContext } from 'react';
import { Context as AlertContext } from 'contexts/alert';
import { Config } from 'configs';
import {
  checkAuthentication,
  getProfile,
  logout as logoutCodeMash,
  login,
  getCompanyInfo,
} from 'api';
import { getErrorMessage } from 'utils/errorHandler';
import { apiActionChooserSettings, isCompanyAllowedForV2ById } from 'utils/apiActionChooser';
import createDataContext from './createDataContext';

const reducer = (state, action) => {
  switch (action.type) {
    case 'onContextInitialized':
      return {
        ...state,
        initialized: true,
        alertController: action.payload.alertController,
      };
    case 'set_initialized':
      return { ...state, authChecked: true };
    case 'set_loading':
      return { ...state, isLoading: action.payload };

    case 'signin':
      return {
        ...state,
        isLoading: state.isLoading,
        errorMessage: null,
        token: action.payload.token,
        user: action.payload.user,
        company: {
          // eslint-disable-next-line no-underscore-dangle
          _id: action.payload.company?._id,
          user: action.payload.company?.user,
          display_name: action.payload.company?.display_name,
          // eslint-disable-next-line no-underscore-dangle
          company: action.payload.company?.company,
          onboarding_completed: action.payload.company?.onboarding_completed,
          payment_link: action.payload.company?.payment_link,
        },
        isSigned: true,
        initialized: true,
        authChecked: true,
      };
    case 'onCompanyUpdated':
      return {
        ...state,
        company: {
          ...state.company,
          display_name: action.payload.name,
        },
      };
    case 'onCompanyOnboardingCompleted':
      return {
        ...state,
        company: {
          ...state.company,
          onboarding_completed: true,
        },
      };
    case 'logout':
      return {
        ...state,
        isSigned: false,
        errorMessage: null,
        token: null,
        user: null,
        company: null,
        initialized: true,
        authChecked: true,
        error: null,
        isLoading: false,
      };
    case 'set_error':
      return { ...state, errorMessage: action.payload };
    case 'clear_error':
      return { ...state, errorMessage: null };
    default:
      return state;
  }
};

const init = (dispatch) => () => {
  const { alertController } = useContext(AlertContext);

  dispatch({
    type: 'onContextInitialized',
    payload: { alertController },
  });
};

const enableVersion2 = () => async () => {
  localStorage.setItem('v2Enabled', 'true');
  apiActionChooserSettings.v2Enabled = true;
  // delete old token from localstorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('tokenV2');
  localStorage.removeItem('userV2');
};

const disableVersion2 = () => async () => {
  localStorage.setItem('v2Enabled', 'false');
  apiActionChooserSettings.v2Enabled = false;
  // delete old token from localstorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('tokenV2');
  localStorage.removeItem('userV2');
};

const initService = (dispatch, state) => async () => {
  const tokenV2 = localStorage.getItem('tokenV2');
  const token = localStorage.getItem('token');

  if (tokenV2) {
    dispatch({ type: 'set_loading', payload: true });
    // const token = localStorage.getItem('tokenV2');

    if (!tokenV2) {
      dispatch({ type: 'set_initialized' });
      dispatch({ type: 'set_loading', payload: false });
      return;
    }

    try {
      const userResponse = await getProfile({
        alertController: state.alertController,
        useV2: true,
      });

      const company = await getCompanyInfo({ useV2: true });

      const user = {
        id: userResponse.id,
        userName: userResponse.email,
        displayName: userResponse.email,
        role: Config.ROLES.AUTHENTICATED,
        language: Config.DEFAULTS.LANGUAGE,
      };

      const userDataAsString = JSON.stringify(user);

      localStorage.setItem('userV2', userDataAsString);
      localStorage.setItem('v2-company-id', company.company);

      dispatch({ type: 'signin', payload: { user, company } });
    } catch (e) {
      Config.SHOW_LOGS && console.log('Error occured', e);

      if (state?.alertController) {
        state.alertController.error(getErrorMessage(e));
      }
    } finally {
      dispatch({ type: 'set_initialized' });
      dispatch({ type: 'set_loading', payload: false });
    }
  } else {
    dispatch({ type: 'set_loading', payload: true });
    // const token = localStorage.getItem('token');

    const userAsString = localStorage.getItem('user');

    if (userAsString) {
      const user = JSON.parse(userAsString);

      const company = await getCompanyInfo({
        token,
        user: user.id,
        alertController: state.alertController,
      });

      // Check if company is v2
      if (isCompanyAllowedForV2ById(company.company)) {
        dispatch({ type: 'set_initialized' });
        dispatch({ type: 'set_loading', payload: false });
        window.location.href = '/logout';
        return;
      }

      dispatch({ type: 'signin', payload: { token, user, company } });
      dispatch({ type: 'set_initialized' });
      dispatch({ type: 'set_loading', payload: false });
      return;
    }

    if (!token) {
      dispatch({ type: 'set_initialized' });
      dispatch({ type: 'set_loading', payload: false });
      return;
    }

    try {
      const response = await checkAuthentication({
        token,
        alertController: state.alertController,
      });

      const userResponse = await getProfile({
        token,
        alertController: state.alertController,
      });

      const company = await getCompanyInfo({ token, user: response.userId });

      // Check if company is v2
      if (isCompanyAllowedForV2ById(company.company)) {
        dispatch({ type: 'set_initialized' });
        dispatch({ type: 'set_loading', payload: false });
        window.location.href = '/logout';
        return;
      }

      const user = {
        id: response.userId,
        userName: userResponse.email,
        displayName: userResponse.displayName,
        role: Config.ROLES.AUTHENTICATED, // TODO: get role from response
        language: userResponse.language ?? Config.DEFAULTS.LANGUAGE,
      };

      const userDataAsString = JSON.stringify(user);

      localStorage.setItem('user', userDataAsString);
      localStorage.removeItem('v2-company-id');

      dispatch({ type: 'signin', payload: { token, user, company } });
    } catch (e) {
      Config.SHOW_LOGS && console.log('Error occured', e);

      if (state?.alertController) {
        state.alertController.error(getErrorMessage(e));
      }

      if (e.name === 'Forbidden') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } finally {
      dispatch({ type: 'set_initialized' });
      dispatch({ type: 'set_loading', payload: false });
    }
  }
};

const signin =
  (dispatch, state) =>
  async ({ email, password }, callback) => {
    if (apiActionChooserSettings.v2Enabled) {
      dispatch({ type: 'set_loading', payload: true });

      try {
        const response = await login({
          email,
          password,
          useV2: true,
          alertController: state.alertController,
        });

        if (!response) {
          throw new Error('Forbidden');
        }

        localStorage.setItem('tokenV2', JSON.stringify(response));

        const company = await getCompanyInfo({ useV2: true });

        const user = {
          id: response.id,
          userName: response.email,
          displayName: response.email,
          role: Config.ROLES.AUTHENTICATED,
        };

        localStorage.setItem('userV2', JSON.stringify(user));
        localStorage.setItem('v2-company-id', company.company);

        dispatch({ type: 'signin', payload: { user, company } });

        callback();
      } catch (e) {
        Config.SHOW_LOGS && console.log('Error occured', e);

        let errorMessage;

        if (e.message === 'Unauthorized') {
          errorMessage = 'Unauthorized';
          dispatch({
            type: 'set_error',
            payload: errorMessage,
          });
        } else if (e.message === 'Forbidden') {
          errorMessage = 'Invalid email address or password';
          dispatch({
            type: 'set_error',
            payload: errorMessage,
          });
        } else if (e.message === 'UserDeactivated') {
          errorMessage = 'User deactivated';
          dispatch({
            type: 'set_error',
            payload: errorMessage,
          });
        } else {
          errorMessage = 'Unknown error';
          dispatch({
            type: 'set_error',
            payload: errorMessage,
          });
        }

        if (state?.alertController) {
          state.alertController.error({ text: errorMessage });
        }
      }
    } else {
      dispatch({ type: 'set_loading', payload: true });

      try {
        const response = await login({
          email,
          password,
          alertController: state.alertController,
        });

        if (response) {
          const { roles } = response || { roles: [] };
          const roleCompany = roles.find((role) => role.name === Config.ROLES.COMPANY);

          if (!roleCompany) {
            throw new Error('Forbidden');
          }
        }

        const token = response.bearerToken;

        const company = await getCompanyInfo({
          token,
          user: response.userId,
          alertController: state.alertController,
        });

        if (isCompanyAllowedForV2ById(company.company)) {
          state.alertController.error({
            text: 'Account is migrated to V2. Please enable V2 and login with a new V2 account.',
          });
          dispatch({ type: 'set_loading', payload: false });
          return;
        }

        const user = {
          id: response.userId,
          userName: response.userName,
          displayName: response.displayName,
          role: Config.ROLES.AUTHENTICATED, // TODO: get role from response
        };

        const userDataAsString = JSON.stringify(user);

        localStorage.setItem('token', token);
        localStorage.setItem('user', userDataAsString);
        localStorage.removeItem('v2-company-id');

        dispatch({ type: 'signin', payload: { token, user, company } });

        // await apiActionChooser(
        //   company.company,
        //   () => {},
        //   async () => {
        //     const loginV2Response = await postLogin({
        //       data: {
        //         username: email,
        //         password: response.userId,
        //       },
        //       path: '/users/v1/login',
        //     });

        //     localStorage.setItem('tokenV2', JSON.stringify(loginV2Response));
        //   },
        // );

        callback();
      } catch (e) {
        Config.SHOW_LOGS && console.log('Error occured', e);

        if (state?.alertController) {
          state.alertController.error(getErrorMessage(e));
        }

        if (e.name === 'Unauthorized') {
          dispatch({
            type: 'set_error',
            payload: 'Unauthorized',
          });
        } else if (e.name === 'Forbidden') {
          dispatch({
            type: 'set_error',
            payload: 'Forbidden',
          });
        } else if (e.name === 'UserDeactivated') {
          dispatch({
            type: 'set_error',
            payload: 'User deactivated',
          });
        } else {
          dispatch({
            type: 'set_error',
            payload: 'Unknown error',
          });
        }
      } finally {
        dispatch({ type: 'set_loading', payload: false });
      }
    }
  };

const logout = (dispatch, state) => async (callback) => {
  localStorage.removeItem('tokenV2');
  localStorage.removeItem('userV2');
  localStorage.removeItem('v2-company-id');

  // V1
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // await logoutCodeMash({ token: state.token, alertController: state.alertController });

  dispatch({ type: 'logout' });

  callback();
};

const onCompanyUpdate = (dispatch) => async (company) => {
  dispatch({ type: 'onCompanyUpdated', payload: company });
};

const onCompanyOnboardingCompleted = (dispatch) => async () => {
  dispatch({ type: 'onCompanyOnboardingCompleted' });
};

const clearErrorMessage = (dispatch) => () => {
  dispatch({ type: 'set_error', payload: null });
};

const initialData = {
  isSigned: false,
  error: null,
  errorMessage: null,
  alertController: null,
  token: null,
  user: null,
  company: null,
  isLoading: false,
  authChecked: false,
  initialized: false,
};

export const { Context, Provider } = createDataContext(
  reducer,
  {
    initService,
    signin,
    logout,
    clearErrorMessage,
    onCompanyUpdate,
    onCompanyOnboardingCompleted,
    enableVersion2,
    disableVersion2,
  },
  { ...initialData },
  null,
  init,
);
